import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { CHANNELS } from "../../data/site";
import { useT } from "@/i18n/I18nContext";

function ChannelBlock({ channel, reverse }) {
  const { t } = useT();
  const tx = (k) => t(`channels.${channel.slug}.${k}`);
  return (
    <article
      data-testid={`channel-${channel.slug}`}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="lg:col-span-6 relative"
      >
        <div className="relative aspect-[4/5] overflow-hidden rounded-sm tt-noise">
          <img
            src={channel.image}
            alt={channel.name}
            className="w-full h-full object-cover transition-transform duration-[1.6s] hover:scale-[1.04]"
            loading="lazy"
          />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(180deg, transparent 40%, rgba(4,8,7,0.85) 100%), linear-gradient(180deg, ${channel.accentSoft} 0%, transparent 50%)`,
            }}
          />
          <div className="absolute top-6 left-6 flex items-center gap-3">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: channel.accent, boxShadow: `0 0 14px ${channel.accent}` }}
            />
            <span className="tt-overline text-[var(--tt-text)]">canal {reverse ? "02" : "01"}</span>
          </div>
          <div className="absolute bottom-6 left-6 right-6">
            <p className="tt-overline" style={{ color: channel.accent }}>
              {channel.handle}
            </p>
            <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[var(--tt-text)] leading-tight">
              {channel.name}
            </h3>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.15 }}
        className="lg:col-span-6"
      >
        <p className="tt-overline mb-6" style={{ color: channel.accent }}>
          {tx("tagline")}
        </p>
        <h4 className="font-serif text-3xl md:text-5xl leading-[1.05] text-[var(--tt-text)]">
          {tx("heading_l")}{" "}
          <em className="italic font-light text-[var(--tt-gold)]">{tx("heading_r")}</em>.
        </h4>
        <p className="mt-6 text-[var(--tt-text-dim)] leading-relaxed max-w-xl font-light">
          {tx("description")}
        </p>

        <ul className="mt-8 grid grid-cols-2 gap-y-3 gap-x-6 max-w-md">
          {tx("pillars").map((p) => (
            <li
              key={p}
              className="flex items-start gap-3 text-[var(--tt-text-dim)] text-sm font-light"
            >
              <span className="mt-[7px] w-3 h-px" style={{ background: channel.accent }} />
              {p}
            </li>
          ))}
        </ul>

        <div className="mt-10 max-w-md">
          <p className="tt-overline mb-4">{tx("community_label")}</p>
          <div className="grid grid-cols-3 gap-4 border-t border-[var(--tt-border)] pt-6">
            {[
              [t("channels.stats.subscribers"), channel.stats.subscribers],
              [t("channels.stats.views"), channel.stats.views],
              [t("channels.stats.videos"), channel.stats.videos],
            ].map(([label, value]) => (
              <div key={label}>
                <p className="font-serif text-2xl md:text-3xl text-[var(--tt-text)]">{value}</p>
                <p className="tt-overline mt-1 text-[10px]">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <a
          href={channel.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`channel-link-${channel.slug}`}
          className="mt-10 inline-flex items-center gap-3 tt-link-underline"
          style={{ color: channel.accent }}
        >
          <span className="tt-overline" style={{ color: channel.accent }}>
            {t("channels.visit")}
          </span>
          <ExternalLink size={14} />
        </a>
      </motion.div>
    </article>
  );
}

export default function ChannelShowcase() {
  const { t } = useT();
  return (
    <section id="canais" data-testid="channels-section" className="tt-section relative">
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-20">
          <div className="max-w-2xl">
            <p className="tt-overline">{t("channels.overline")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
              {t("channels.heading_1")}{" "}
              <em className="italic text-[var(--tt-gold)]">{t("channels.heading_2")}</em>.
            </h2>
          </div>
          <p className="text-[var(--tt-text-dim)] max-w-md font-light">
            {t("channels.subheading")}
          </p>
        </div>

        <div className="space-y-32">
          {CHANNELS.map((c, i) => (
            <ChannelBlock key={c.slug} channel={c} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
